import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  MapPin, Briefcase, Building2, Calendar, 
  ArrowLeft, ExternalLink, GraduationCap, 
  Clock, DollarSign, CheckCircle2, ListChecks,
  Printer, Heart, Loader2
} from 'lucide-react';

import PageLayout from '../components/PageLayout';

const JobDetails = ({ jobsData }) => {
  const { id } = useParams(); 
  const navigate = useNavigate();
  const [isSaved, setIsSaved] = useState(false);

  // 1. Logic to find the job within the city-keyed object
  // We flatten all city arrays and find the one matching the ID
  const job = React.useMemo(() => {
    if (!jobsData) return null;
    return Object.values(jobsData).flat().find(j => j.id === id);
  }, [jobsData, id]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const handlePrint = () => window.print();
  const handleToggleSave = () => setIsSaved(!isSaved);

  // If data is still being fetched in App.js
  if (!jobsData || Object.keys(jobsData).length === 0) {
    return (
      <PageLayout>
        <div className="flex h-[60vh] items-center justify-center">
          <Loader2 className="w-10 h-10 text-[#00c19e] animate-spin" />
        </div>
      </PageLayout>
    );
  }

  if (!job) {
    return (
      <PageLayout>
        <div className="text-center py-20 px-4">
          <h2 className="text-2xl font-bold text-slate-800">Job not found</h2>
          <Link to="/" className="text-[#00c19e] font-bold hover:underline mt-6 inline-flex items-center gap-2">
            <ArrowLeft size={18} /> Return to Listings
          </Link>
        </div>
      </PageLayout>
    );
  }

  // Helper for highlights from the normalized 'raw' object
  const highlights = job.raw?.job_highlights || {};
  const qualifications = job.raw?.job_required_skills || 
                       (highlights.Qualifications) || [];

  return (
    <PageLayout>
      <div className="w-full py-8 px-4 max-w-[1400px] mx-auto print:p-0">
        
        {/* ACTION BAR */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8 print:hidden">
          <button 
            onClick={() => navigate(-1)} 
            className="flex items-center gap-2 text-slate-500 hover:text-[#00c19e] font-bold transition-colors group"
          >
            <ArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
            <span>Back to Jobs</span>
          </button>
          
          <div className="flex items-center gap-3">
            <button onClick={handlePrint} className="p-2.5 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 shadow-sm transition-all">
              <Printer size={20} />
            </button>
            <button onClick={handleToggleSave} className={`p-2.5 rounded-xl border shadow-sm transition-all ${isSaved ? 'bg-red-50 border-red-100 text-red-500' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
              <Heart size={20} fill={isSaved ? "currentColor" : "none"} />
            </button>
          </div>
        </div>

        {/* 1. HEADER SECTION */}
        <div className="bg-white rounded-3xl p-8 border border-slate-200 shadow-sm mb-8 print:border-none print:shadow-none">
          <div className="flex flex-col lg:flex-row justify-between items-start gap-8">
            <div className="flex gap-6 items-start">
              <div className="w-20 h-20 bg-slate-50 rounded-2xl flex items-center justify-center overflow-hidden border border-slate-100 flex-shrink-0">
                {job.logo ? (
                  <img src={job.logo} alt={job.company} className="w-full h-full object-contain p-2" />
                ) : (
                  <Building2 size={36} className="text-slate-300" />
                )}
              </div>
              
              <div>
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  <span className="bg-[#00c19e]/10 text-[#00a99d] px-3 py-1 rounded-lg text-xs font-bold uppercase tracking-wider">
                    {job.type}
                  </span>
                  <span className="text-slate-400 text-sm flex items-center gap-1 font-medium">
                    <Clock size={14} /> Posted {new Date(job.postedAt).toLocaleDateString()}
                  </span>
                </div>
                <h1 className="text-3xl md:text-4xl font-black text-slate-900 mb-2 leading-tight">
                  {job.title}
                </h1>
                <p className="text-xl font-bold text-[#00a99d] mb-4">{job.company}</p>
                <div className="flex items-center gap-2 text-slate-600 font-bold">
                  <MapPin size={20} className="text-orange-500" /> {job.location.full}
                </div>
              </div>
            </div>

            <div className="w-full lg:w-auto flex flex-col gap-3 print:hidden">
              <a href={job.applyUrl} target="_blank" rel="noopener noreferrer" className="w-full lg:w-64 bg-[#00c19e] text-white px-8 py-4 rounded-2xl font-bold hover:bg-[#00a99d] shadow-xl shadow-[#00c19e]/20 flex items-center justify-center gap-2 transition-all transform hover:-translate-y-1 text-center">
                Apply Now <ExternalLink size={18} />
              </a>
            </div>
          </div>
        </div>

        {/* 2. CONTENT GRID */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8 space-y-8">
            
            {/* Responsibilities */}
            {highlights.Responsibilities && highlights.Responsibilities.length > 0 && (
              <section className="bg-white p-8 rounded-3xl border border-slate-200 shadow-sm">
                <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3 border-b border-slate-50 pb-4">
                  <ListChecks className="text-[#00c19e]" /> Key Responsibilities
                </h2>
                <ul className="space-y-4">
                  {highlights.Responsibilities.map((item, i) => (
                    <li key={i} className="flex items-start gap-4 text-slate-600 leading-relaxed text-lg">
                      <div className="mt-2.5 w-2.5 h-2.5 bg-[#00c19e] rounded-full flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Qualifications */}
            {qualifications && qualifications.length > 0 && (
              <section className="bg-white p-8 rounded-3xl border border-slate-200 shadow-sm">
                <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3 border-b border-slate-50 pb-4">
                  <GraduationCap className="text-[#00c19e]" /> Qualifications
                </h2>
                <div className="grid grid-cols-1 gap-4">
                  {qualifications.map((item, i) => (
                    <div key={i} className="flex items-start gap-4 p-5 rounded-2xl bg-slate-50 border border-slate-100 text-slate-700">
                      <CheckCircle2 size={24} className="text-[#00c19e] flex-shrink-0" />
                      <span className="font-bold">{item}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Full Description */}
            <section className="bg-white p-8 rounded-3xl border border-slate-200 shadow-sm">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">Job Description</h2>
              <div className="text-slate-600 leading-relaxed whitespace-pre-line text-lg">
                {job.description}
              </div>
            </section>
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-4 space-y-6">
            <div className="bg-slate-900 text-white p-8 rounded-[2.5rem] shadow-2xl sticky top-24 print:static print:text-black">
              <h3 className="font-bold mb-8 text-xl border-b border-white/10 pb-4">Job Summary</h3>
              <div className="space-y-6">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3 text-slate-400"><DollarSign size={20} /><span>Salary</span></div>
                  <span className="font-bold text-[#00c19e] text-right">
                    {job.salary}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 text-slate-400"><Briefcase size={20} /><span>Type</span></div>
                  <span className="font-bold capitalize">{job.type}</span>
                </div>
              </div>

              {job.raw?.job_benefits && job.raw.job_benefits.length > 0 && (
                <div className="mt-10 pt-8 border-t border-white/10">
                  <h4 className="text-sm font-bold uppercase tracking-widest text-slate-500 mb-5">Benefits</h4>
                  <div className="flex flex-wrap gap-2">
                    {job.raw.job_benefits.map(b => (
                      <span key={b} className="bg-white/5 border border-white/10 px-4 py-1.5 rounded-xl text-xs font-semibold capitalize">
                        {b.replace(/_/g, ' ')}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </aside>
        </div>
      </div>
    </PageLayout>
  );
};

export default JobDetails;